/**
 * Modal de cierre de tenencia — Sprint 12 S12-06
 * (migrado al portal en Sprint 13 S13-07).
 *
 * Form con fecha de cierre + motivo opcional + confirmacion explicita.
 * Consume POST /tenencias/{id}/cerrar de S11-V2-13.
 *
 * Doctrina (convencion #14): si la persona queda sin tenencias activas
 * tras este cierre, el backend la marca como `activo=False`
 * automaticamente (ex-vecino). El modal advierte explicitamente.
 */
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { cerrarTenencia } from '../../lib/api/tenencias';
import type { TenenciaConPersonaLote } from '../../lib/api/types';
import { ETIQUETA_TIPO_TENENCIA } from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { InputFecha } from '../../components/ui/InputFecha';
import { formatRut } from '../../lib/format';
import { hoy } from '../../lib/tiempo';

interface Props {
  tenencia: TenenciaConPersonaLote;
  onCerrar: () => void;
  onCerrada: () => void;
}

function formatFechaCorta(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, anio, mes, dia] = m;
  return `${dia}-${mes}-${anio}`;
}

export function ModalCerrarTenencia({
  tenencia,
  onCerrar,
  onCerrada,
}: Props) {
  const fechaHoy = hoy();
  const [fechaCierre, setFechaCierre] = useState(fechaHoy);
  const [motivo, setMotivo] = useState('');
  // "Corrección de registro" no es un cierre normal: la tenencia nunca
  // debió existir, así que NADIE se fue de la casa. El backend usa este
  // motivo exacto para saltarse la cascada que inactiva residentes, baja
  // accesos y devuelve los llaveros de la casa a la bolsa.
  const [esCorreccion, setEsCorreccion] = useState(false);
  const [confirmado, setConfirmado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!confirmado) {
      setError('Marca la confirmación antes de cerrar.');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await cerrarTenencia(tenencia.id, {
        fecha_cierre: fechaCierre,
        // El contrato es la BANDERA, no un literal en el texto libre: ese
        // campo llega sin validar desde el endpoint del vecino.
        es_correccion_registro: esCorreccion,
        motivo_cierre: esCorreccion ? null : motivo.trim() || null,
        // Lo que escriba el operador no se pierde: viaja como nota.
        notas_cierre: esCorreccion ? motivo.trim() || null : null,
      });
      onCerrada();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' && err.body && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red al cerrar la tenencia.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-cerrar-tenencia-titulo"
    >
      <div
        className="bg-bg-elevated border-2 border-admin-red rounded-md p-6 max-w-xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2
              id="modal-cerrar-tenencia-titulo"
              className="text-xl font-medium text-text-primary"
            >
              Cerrar tenencia
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              {tenencia.persona_nombre_completo} · {formatRut(tenencia.persona_rut)}
            </p>
            <p className="text-xs text-text-muted mt-1">
              Casa {tenencia.lote_sector} {tenencia.lote_identificador} ·{' '}
              {ETIQUETA_TIPO_TENENCIA[tenencia.tipo] ?? tenencia.tipo} · desde{' '}
              {formatFechaCorta(tenencia.desde)}
            </p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="text-text-secondary hover:text-text-primary text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="fecha-cierre"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Fecha efectiva del cierre
            </label>
            <InputFecha
              id="fecha-cierre"
              requerido
              min={tenencia.desde}
              value={fechaCierre}
              onChange={setFechaCierre}
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-admin-red focus:ring-1 focus:ring-admin-red/30 outline-none"
            />
            <p className="text-xs text-text-muted mt-1">
              Debe ser igual o posterior al inicio de la tenencia ({formatFechaCorta(tenencia.desde)}).
            </p>
          </div>

          <div>
            <label
              htmlFor="motivo-cierre"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Motivo del cierre (opcional)
            </label>
            <input
              id="motivo-cierre"
              type="text"
              maxLength={200}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej. venta, término de arriendo, fallecimiento, cesión"
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 placeholder:text-text-muted focus:border-admin-red focus:ring-1 focus:ring-admin-red/30 outline-none"
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-text-primary cursor-pointer mb-1">
            <input
              type="checkbox"
              checked={esCorreccion}
              onChange={(e) => setEsCorreccion(e.target.checked)}
              className="mt-1"
            />
            <span>
              Esta tenencia <strong>nunca debió existir</strong> (corrección de
              registro, no se fue nadie de la casa)
            </span>
          </label>

          <div
            className={
              esCorreccion
                ? 'bg-bg-sunken border-l-4 border-text-muted/40 p-3 rounded-sm'
                : 'bg-admin-red/[0.08] border-l-4 border-admin-red p-3 rounded-sm'
            }
          >
            <p className="text-sm text-text-primary mb-2">
              {esCorreccion ? (
                <>
                  <strong>Corrección de registro:</strong> se cierra solo
                  esta tenencia. Los demás residentes de la casa{' '}
                  <strong>pasan a la tenencia que queda</strong> conservando
                  sus llaveros, controles y accesos, y la persona no se marca
                  como ex-vecino si sigue viviendo ahí. Úsalo cuando el dato
                  estaba mal cargado, no cuando alguien deja el barrio.
                </>
              ) : (
                <>
                  <strong>Importante:</strong> al cerrar esta tenencia, si la
                  persona no tiene otras tenencias activas ni sigue viviendo en
                  alguna casa, se marcará como ex-vecino y dejará de recibir
                  comunicaciones operativas del barrio (cron de morosidad,
                  cierres, etc.). La persona no se elimina; queda en sistema
                  para reportes históricos.
                </>
              )}
            </p>
            <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
              <input
                type="checkbox"
                checked={confirmado}
                onChange={(e) => setConfirmado(e.target.checked)}
                className="rounded-sm accent-admin-red"
              />
              <span>
                Confirmo el cierre de esta tenencia con efecto a la fecha indicada.
              </span>
            </label>
          </div>

          {error && (
            <div
              className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="ghost" onClick={onCerrar}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={enviando || !confirmado}
            >
              {enviando ? 'Cerrando…' : 'Cerrar tenencia'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
